// Custom Hook for Quiz History Tracking
// Tích hợp QuizHistoryService và AchievementService vào quiz components

import { useState, useCallback } from 'react';
import { quizHistoryService } from '../services/studyHistory/QuizHistoryService';
import { achievementService } from '../services/studyHistory/AchievementService';
import { useAuth } from '../contexts/AuthContext';
import {
  CreateQuizResultData,
  QuizResult,
  QuizType,
  QuizAnswer,
  Achievement
} from '../types/learningHistory';

interface UseQuizTrackingOptions {
  quizId: string;
  lessonId?: string;
  quizTitle: string;
  quizType?: QuizType;
  timeLimitSeconds?: number;
  passingScore?: number;
}

interface QuizTrackingData {
  startTime: number;
  answers: QuizAnswer[];
  isSaving: boolean;
  lastResult: QuizResult | null;
  newAchievements: Achievement[];
}

export function useQuizTracking(options: UseQuizTrackingOptions) {
  const { user } = useAuth();
  const {
    quizId,
    lessonId,
    quizTitle,
    quizType = 'lesson',
    timeLimitSeconds,
    passingScore = 70
  } = options;

  const [trackingData, setTrackingData] = useState<QuizTrackingData>({
    startTime: Date.now(),
    answers: [],
    isSaving: false,
    lastResult: null,
    newAchievements: []
  });

  // Record answer
  const recordAnswer = useCallback((answer: QuizAnswer) => {
    setTrackingData(prev => ({
      ...prev,
      answers: [...prev.answers, answer]
    }));

    console.log(`📝 [useQuizTracking] Answer recorded:`, answer);
  }, []);

  // Save quiz result
  const saveQuizResult = async (
    score: number,
    totalQuestions: number,
    correctAnswers: number,
    wrongAnswers: number,
    skippedAnswers: number,
    answers: QuizAnswer[],
    metadata?: any,
    fullscreenExitCount?: number
  ): Promise<{ result: QuizResult; achievements: Achievement[] } | null> => {
    if (!user?.id) {
      console.error('❌ [useQuizTracking] User not logged in');
      return null;
    }

    setTrackingData(prev => ({ ...prev, isSaving: true }));

    try {
      const timeSpent = Math.floor((Date.now() - trackingData.startTime) / 1000);
      const passed = score >= passingScore;

      console.log(`💾 [useQuizTracking] Saving quiz result:`, {
        quizId,
        score,
        passed,
        timeSpent,
        fullscreenExitCount: fullscreenExitCount || 0
      });

      // Prepare quiz result data
      const quizResultData: CreateQuizResultData = {
        userId: user.id,
        quizId,
        lessonId,
        quizTitle,
        quizType,
        score,
        totalQuestions,
        correctAnswers,
        wrongAnswers,
        skippedAnswers,
        timeSpent,
        timeLimitSeconds,
        passed,
        passingScore,
        answers,
        metadata,
        fullscreenExitCount: fullscreenExitCount || 0
      };

      // Save quiz result
      const result = await quizHistoryService.saveQuizResult(quizResultData);

      console.log(`✅ [useQuizTracking] Quiz result saved:`, result);

      // Check for achievements
      const newAchievements = await achievementService.checkAfterQuizComplete(
        user.id,
        quizId,
        score,
        timeSpent,
        timeLimitSeconds,
        score === 100
      );

      if (newAchievements.length > 0) {
        console.log(`🏆 [useQuizTracking] Unlocked ${newAchievements.length} achievements:`, newAchievements);
      }

      setTrackingData(prev => ({
        ...prev,
        isSaving: false,
        lastResult: result,
        newAchievements
      }));

      return { result, achievements: newAchievements };
    } catch (error) {
      console.error('❌ [useQuizTracking] Failed to save quiz result:', error);
      setTrackingData(prev => ({ ...prev, isSaving: false }));
      return null;
    }
  };

  // Get quiz history
  const getQuizHistory = async (filters?: any): Promise<QuizResult[]> => {
    if (!user?.id) return [];

    try {
      const history = await quizHistoryService.getQuizHistory(user.id, filters);
      console.log(`📚 [useQuizTracking] Loaded ${history.length} quiz results`);
      return history;
    } catch (error) {
      console.error('❌ [useQuizTracking] Failed to get quiz history:', error);
      return [];
    }
  };

  // Get latest attempt
  const getLatestAttempt = async (): Promise<QuizResult | null> => {
    if (!user?.id) return null;

    try {
      const latest = await quizHistoryService.getLatestAttempt(user.id, quizId);
      console.log(`📄 [useQuizTracking] Latest attempt:`, latest);
      return latest;
    } catch (error) {
      console.error('❌ [useQuizTracking] Failed to get latest attempt:', error);
      return null;
    }
  };

  // Get all attempts
  const getAllAttempts = async (): Promise<QuizResult[]> => {
    if (!user?.id) return [];

    try {
      const attempts = await quizHistoryService.getAllAttempts(user.id, quizId);
      console.log(`📊 [useQuizTracking] Found ${attempts.length} attempts for this quiz`);
      return attempts;
    } catch (error) {
      console.error('❌ [useQuizTracking] Failed to get all attempts:', error);
      return [];
    }
  };

  // Get quiz statistics
  const getQuizStatistics = async () => {
    if (!user?.id) return null;

    try {
      const stats = await quizHistoryService.getQuizStatistics(user.id);
      console.log(`📊 [useQuizTracking] Quiz statistics:`, stats);
      return stats;
    } catch (error) {
      console.error('❌ [useQuizTracking] Failed to get quiz statistics:', error);
      return null;
    }
  };

  // Get improvement trend
  const getImprovementTrend = async () => {
    if (!user?.id || !lessonId) return null;

    try {
      const trend = await quizHistoryService.getImprovementTrend(user.id, lessonId);
      console.log(`📈 [useQuizTracking] Improvement trend:`, trend);
      return trend;
    } catch (error) {
      console.error('❌ [useQuizTracking] Failed to get improvement trend:', error);
      return null;
    }
  };

  // Reset tracking (for retake)
  const resetTracking = () => {
    setTrackingData({
      startTime: Date.now(),
      answers: [],
      isSaving: false,
      lastResult: null,
      newAchievements: []
    });
    console.log(`🔄 [useQuizTracking] Tracking reset for retake`);
  };

  return {
    trackingData,
    recordAnswer,
    saveQuizResult,
    getQuizHistory,
    getLatestAttempt,
    getAllAttempts,
    getQuizStatistics,
    getImprovementTrend,
    resetTracking
  };
}

